import * as React from 'react';

import { cn } from '@/lib/utils';

/**
 * Icons for the concepts Lucide cannot express.
 *
 * The rest of the app uses Lucide, and it should: it is complete, consistent
 * and well drawn. These six exist because the product's central objects have
 * no generic equivalent — a tanker is not a box truck, a delivery note is not
 * a page, a volume variance is not a warning triangle — and the interface was
 * approximating them with borrowed glyphs.
 *
 * They are drawn on Lucide's own grid so they sit beside it without a seam:
 *   24×24 viewBox · 2px stroke · round caps and joins · no fill ·
 *   content inset ~2px from the edge · `currentColor`.
 *
 * That constraint is the whole game: a custom icon at the wrong stroke weight
 * reads as a bolted-on foreign object, which is worse than the approximation
 * it replaced.
 */

const createIcon = (name, children) => {
  const Icon = ({ size = 24, strokeWidth = 2, className, ...props }) => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      // `lucide` keeps parity with the library's own class, so any global
      // icon styling applies to these too.
      className={cn('lucide', className)}
      aria-hidden="true"
      {...props}
    >
      {children}
    </svg>
  );
  Icon.displayName = name;
  return Icon;
};

/**
 * Citerne — a tanker, not a box truck.
 * The cylinder and its weld seam are what make it a tanker; Lucide's `Truck`
 * draws a rectangular body, which is a different vehicle.
 */
export const Citerne = createIcon(
  'Citerne',
  <>
    {/* The chassis stroke was redundant with the cab's own baseline and only
        added density at 16px. The capsule plus its weld seam is what makes
        this a tanker; everything else is scaffolding. */}
    <path d="M2 15v-4a1 1 0 0 1 1-1h4" />
    <rect x="6" y="6" width="16" height="9" rx="4.5" />
    <path d="M10 6v9" />
    <circle cx="8" cy="18" r="2" />
    <circle cx="18" cy="18" r="2" />
  </>,
);

/**
 * Convoi — several trucks moving as one unit.
 * Previously a briefcase, which says "business", not "column of tankers".
 * The repetition is the meaning: three bodies on one road.
 */
export const Convoi = createIcon(
  'Convoi',
  <>
    {/* Two units, not three: at 16px — the size this app actually renders
        icons at — three bodies collapsed into an unreadable blob. Two still
        reads as "more than one vehicle", which is the whole meaning. */}
    <rect x="2" y="8" width="8" height="7" rx="1.5" />
    <path d="M13 15v-5a1 1 0 0 1 1-1h3l3 3.5V15" />
    <circle cx="6" cy="18" r="1.8" />
    <circle cx="17" cy="18" r="1.8" />
  </>,
);

/**
 * Jaugeage — dipping a tank to read its level.
 * The measuring act, which is this product's whole thesis: a rod through the
 * hatch and a level read against it. Lucide's `Gauge` is a dial, a different
 * kind of measurement entirely.
 */
export const Jaugeage = createIcon(
  'Jaugeage',
  <>
    <path d="M4 8h16" />
    <path d="M5 8v11a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8" />
    <path d="M12 3v10" />
    <path d="M10 5h4" />
    <path d="M5 16h14" />
  </>,
);

/**
 * Écart de volume — measured against expected.
 * Two levels in one vessel and the gap between them. This was an
 * `AlertTriangle`, identical to every other warning, so the app's central
 * business concept looked like generic noise.
 */
export const EcartVolume = createIcon(
  'EcartVolume',
  <>
    {/* The two arrowheads that used to mark the gap turned to noise at 16px.
        A single stem between the two levels says the same thing and
        survives. */}
    <path d="M4 4h16" />
    <path d="M5 4v15a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V4" />
    <path d="M8 10h8" />
    <path d="M8 16h8" />
    <path d="M12 10v6" />
  </>,
);

/**
 * Bon de livraison — a signed proof of delivery, not a page of text.
 * The signature is the point: a BL has evidentiary value, which is why the
 * app tracks missing ones.
 */
export const BonLivraison = createIcon(
  'BonLivraison',
  <>
    <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
    <path d="M14 2v5h5" />
    <path d="M8 11h6" />
    <path d="M8 16.5c1.2-1.6 2.4 1.6 3.6 0s2.4 1.6 3.6 0" />
  </>,
);

/**
 * Quota — consumption against a ceiling.
 * Columns rising toward a hard limit. A dial (`Gauge`) shows a rate; a quota
 * is a cap, and the cap is what must be visible.
 */
export const Quota = createIcon(
  'Quota',
  <>
    <path d="M3 4h18" />
    <path d="M7 20v-7" />
    <path d="M12 20V8" />
    <path d="M17 20v-4" />
    <path d="M3 20h18" />
  </>,
);

const domainIcons = {
  Citerne,
  Convoi,
  Jaugeage,
  EcartVolume,
  BonLivraison,
  Quota,
};

export default domainIcons;
